import { type FC, useState, useEffect, useMemo, useRef } from "react";
import {
  Select,
  Button,
  Alert,
  Badge,
  TextInput,
  Pagination,
  ToggleSwitch,
} from "flowbite-react";
import { HiX, HiSearch } from "react-icons/hi";
import {
  getContractCategories,
  type ContractCategory,
} from "../../services/contract-categories";
import {
  getContractTypesByCategory,
  type ContractType,
  type ContractCompany,
  getDetailsForContractTypes,
} from "../../services/contract-types";
import CompactContractTypeCard from "./CompactContractTypeCard";
import SignaturePad from "./SignaturePad";
import ContractConfirmationModal from "./ContractConfirmationModal";
import ContractSuccessModal from "./ContractSuccessModal";
import {
  saveContract,
  saveElectricityContractDetails,
  saveGasContractDetails,
} from "../../services/contracts"; // eslint-disable-line
import { getWorkerIdForUser } from "../../services/workers";
import { supabase, TABLES } from "../../lib/supabase";
import { useAuth } from "../../contexts/AuthContext";
import { useNotification } from "../../contexts/NotificationContext";
import {
  generateContractPdf,
  determinePdfTemplate,
} from "../../lib/pdf/contractPdfGenerator";
import { sendContractEmailBatchWithManagementPriority } from "../../lib/email/resendClient";
import { getCurrentDateFormatted } from "../../lib/pdf/fieldMapper";
import type { ContractPdfData } from "../../types/pdf";
import { type WorkerSignatureData } from "../../utils/contractDataCache";
import { getWorkerData } from "../../utils/workerData";
import { downloadSignatureAsBase64 } from "../../utils/signatureStorage";
import {
  clearNewContractModalDraft,
  clearNewContractModalOpenState,
  getNewContractModalDraft,
  isUpdateReloadSessionActive,
  saveNewContractModalDraft,
} from "../../utils/updateReloadDraft";
import {
  clearPersistentNewContractModalDraft,
  getPersistentNewContractModalDraft,
  savePersistentNewContractModalDraft,
} from "../../utils/newContractModalDraft";
import { createEmptyUtilityContractDetails } from "../../contexts/ContractContext";

interface Customer {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone_number: string;
  birthdate: string;
  notes: string;
  gender: number | null; // 1=Herr (male), 2=Frau (female), 3=Divers (other)
  address: {
    id: number;
    street: string;
    house_number: string;
    zip_code: string;
    city: string;
    country: string;
  };
}

interface NewContractModalProps {
  show: boolean;
  onClose: () => void;
  onSuccess: () => void;
  customer: Customer;
}

// Helper function to validate birth date and check if person is 18+ years old
const validateBirthDateAge = (
  birthDateString: string,
  minAge: number = 18
): { isValid: boolean; message: string; age?: number } => {
  if (!birthDateString) {
    return { isValid: false, message: "Geburtsdatum erforderlich" };
  }

  try {
    // Handle dd.mm.yyyy format
    let dateObj: Date;
    if (/^\d{1,2}\.\d{1,2}\.\d{4}$/.test(birthDateString)) {
      const parts = birthDateString.split(".");
      if (parts.length !== 3) {
        return { isValid: false, message: "Ungültiges Datumsformat" };
      }
      const [day, month, year] = parts;
      // JavaScript months are 0-indexed
      dateObj = new Date(
        parseInt(year || "0"),
        parseInt(month || "0") - 1,
        parseInt(day || "0")
      );
    } else {
      // Handle yyyy-mm-dd format from date picker
      dateObj = new Date(birthDateString);
    }

    if (isNaN(dateObj.getTime())) {
      return { isValid: false, message: "Ungültiges Datum" };
    }

    // Calculate age
    const today = new Date();
    let age = today.getFullYear() - dateObj.getFullYear();
    const monthDiff = today.getMonth() - dateObj.getMonth();

    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < dateObj.getDate())
    ) {
      age--;
    }

    // Validate minimum age
    if (age < minAge) {
      return {
        isValid: false,
        message: `Das Mindestalter von ${minAge} Jahren ist nicht erreicht`,
        age,
      };
    }

    // Future date check
    if (dateObj > today) {
      return { isValid: false, message: "Geburtsdatum liegt in der Zukunft" };
    }

    return { isValid: true, message: "", age };
  } catch (error) {
    return { isValid: false, message: "Fehler bei der Datumsvalidierung" };
  }
};

const NewContractModal: FC<NewContractModalProps> = ({
  show,
  onClose,
  onSuccess,
  customer,
}) => {
  const { user } = useAuth();
  const { showNotification } = useNotification();
  const [categories, setCategories] = useState<ContractCategory[]>([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [categoryError, setCategoryError] = useState<string | null>(null);
  const [contractTypes, setContractTypes] = useState<
    (ContractType & { company_details: ContractCompany })[]
  >([]);
  const [loadingTypes, setLoadingTypes] = useState(false);
  const [selectedContractTypes, setSelectedContractTypes] = useState<
    (ContractType & { company_details: ContractCompany })[]
  >([]);
  const [showSignaturePad, setShowSignaturePad] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [signature, setSignature] = useState<string | null>(null); // Storage path
  const [customerSignatureDataUrl, setCustomerSignatureDataUrl] = useState<
    string | null
  >(null); // Cached signature data URL
  // PDF and email states
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successModalMessage, setSuccessModalMessage] = useState("");
  const [successModalSuccess, setSuccessModalSuccess] = useState(true);
  const [successModalActionLabel, setSuccessModalActionLabel] = useState<
    string | undefined
  >(undefined);

  // State to track successfully saved contracts to prevent duplicates on retry
  const [savedContractsData, setSavedContractsData] = useState<{
    success: boolean;
    contractRefs: {
      contractTypeId: number;
      contractType: string;
      contractId: number;
    }[];
  } | null>(null);
  // State for bank details
  const [bankDetails, setBankDetails] = useState("");
  const [bankDetailsFocused, setBankDetailsFocused] = useState(false);
  // Search and pagination state
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 9; // Show 9 items per page (3x3 grid)

  // Birth date state
  const [useDateTextInput, setUseDateTextInput] = useState<boolean>(() => {
    const savedPreference = localStorage.getItem("birthDateInputPreference");
    return savedPreference ? savedPreference === "text" : false;
  });
  const [birthDateText, setBirthDateText] = useState("");

  // Birth date validation state
  const [birthDateValidationError, setBirthDateValidationError] = useState<
    string | null
  >(null);

  // Gender state
  const [gender, setGender] = useState<number | null>(null);

  // Electricity (Strom) contract details state
  const [electricityDetails, setElectricityDetails] = useState(() =>
    createEmptyUtilityContractDetails()
  );

  // Gas contract details state
  const [gasDetails, setGasDetails] = useState(() =>
    createEmptyUtilityContractDetails()
  );

  // Date validation states
  const [electricityDateError, setElectricityDateError] = useState<
    string | null
  >(null);
  const [gasDateError, setGasDateError] = useState<string | null>(null);

  // Worker data state (for sales partner company, employee name, and signature)
  const [workerData, setWorkerData] = useState<WorkerSignatureData | null>(
    null
  );
  const hasAttemptedDraftRestoreRef = useRef(false);
  const restoredPageRef = useRef<number | null>(null);

  // Validation function to check if date is after today
  const validateDeliveryDate = (dateString: string): boolean => {
    if (!dateString) return true; // Allow empty for now, will be caught by required validation
    const selectedDate = new Date(dateString);
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day for fair comparison
    return selectedDate > today;
  };

  // Helper function to check if a value is filled (handles strings and numbers)
  const isFilled = (value: string | number): boolean => {
    if (typeof value === "string") {
      return value.trim() !== "";
    }
    return value !== null && value !== undefined && !isNaN(Number(value));
  };

  // Helper function to check if a contract type is a gas contract
  // Matches: "gas" as separate word, or "erdgas"/"naturgas" anywhere in the name
  const isGasContract = (contractTypeName: string): boolean => {
    const lowerName = contractTypeName.toLowerCase();

    // Check if contains "erdgas" anywhere (e.g., "Erdgas", "Erdgas24", "Erdgasfix24")
    if (lowerName.includes("erdgas")) {
      return true;
    }

    // Check if contains "naturgas" anywhere (e.g., "Naturgas", "Naturgas24")
    if (lowerName.includes("naturgas")) {
      return true;
    }

    // Check if "gas" appears as a standalone word (word boundary check)
    // Matches "Gas", "Gasag Gas Fix 24" but NOT "Megastream"
    if (/\bgas\b/i.test(contractTypeName)) {
      return true;
    }

    return false;
  };

  // Helper function to check if electricity details are filled (at least the required fields)
  const hasElectricityDetailsFilled = (): boolean => {
    return (
      isFilled(electricityDetails.previous_provider_name) &&
      isFilled(electricityDetails.previous_customer_number) &&
      isFilled(electricityDetails.annual_consumption_kwh) &&
      isFilled(electricityDetails.meter_number) &&
      isFilled(electricityDetails.unit_price_ct_kwh) &&
      isFilled(electricityDetails.base_price_eur_month) &&
      // Date is required ONLY if "as soon as possible" is NOT checked
      (electricityDetails.as_soon_as_possible ||
        isFilled(electricityDetails.delivery_start_date))
    );
  };

  // Helper function to check if gas details are filled (at least the required fields)
  const hasGasDetailsFilled = (): boolean => {
    return (
      isFilled(gasDetails.previous_provider_name) &&
      isFilled(gasDetails.previous_customer_number) &&
      isFilled(gasDetails.annual_consumption_kwh) &&
      isFilled(gasDetails.meter_number) &&
      isFilled(gasDetails.unit_price_ct_kwh) &&
      isFilled(gasDetails.base_price_eur_month) &&
      // Date is required ONLY if "as soon as possible" is NOT checked
      (gasDetails.as_soon_as_possible ||
        isFilled(gasDetails.delivery_start_date))
    );
  };

  const isStadtwerkeDessauCategory = (category: string): boolean =>
    category.toLowerCase().includes("stadtwerke dessau");

  // Check if forms should have required fields (only for Stadtwerke Dessau)
  const isStadtwerkeDessau = isStadtwerkeDessauCategory(selectedCategory);

  const getRequiredUtilityType = (
    contractTypeName: string
  ): "electricity" | "gas" | "none" => {
    if (!isStadtwerkeDessauCategory(selectedCategory)) {
      return "none";
    }
    if (isGasContract(contractTypeName)) {
      return "gas";
    }
    if (contractTypeName.toLowerCase().includes("strom")) {
      return "electricity";
    }
    return "none";
  };

  const getMissingRequiredUtilityDetail = (
    selectedTypes: (ContractType & { company_details: ContractCompany })[]
  ): { detailType: "gas" | "electricity"; contractType: string } | null => {
    for (const selectedType of selectedTypes) {
      const requiredType = getRequiredUtilityType(selectedType.type);
      if (requiredType === "gas" && !hasGasDetailsFilled()) {
        return { detailType: "gas", contractType: selectedType.type };
      }
      if (requiredType === "electricity" && !hasElectricityDetailsFilled()) {
        return { detailType: "electricity", contractType: selectedType.type };
      }
    }
    return null;
  };

  // Conversion helpers for database - ensure no empty strings are sent
  const convertToNumber = (value: string | number | undefined): number => {
    if (value === undefined || value === null || value === "") {
      return 0;
    }
    const num = typeof value === "string" ? parseFloat(value) : value;
    return isNaN(num) ? 0 : num;
  };

  const convertToNullableNumber = (
    value: string | number | undefined
  ): number | null => {
    if (value === undefined || value === null || value === "") {
      const nullValue: number | null = null;
      return nullValue;
    }
    const num = typeof value === "string" ? parseFloat(value) : value;
    return isNaN(num) ? null : num;
  };

  const convertToNullableString = (
    value: string | undefined
  ): string | null => {
    if (value === undefined || value === null || value.trim() === "") {
      const nullValue: string | null = null;
      return nullValue;
    }
    return value.trim();
  };

  // Save preference when toggle changes
  const handleToggleChange = (checked: boolean) => {
    setUseDateTextInput(checked);
    localStorage.setItem("birthDateInputPreference", checked ? "text" : "date");
  };

  // Format birth date for display
  const formatBirthDateForDisplay = (dateString: string): string => {
    try {
      if (!dateString) return "";

      // Check if already in dd.mm.yyyy format
      if (/^\d{1,2}\.\d{1,2}\.\d{4}$/.test(dateString)) {
        // Ensure 2 digit day and month
        const parts = dateString.split(".");
        if (parts.length !== 3) return dateString;

        const day = parts[0] || "";
        const month = parts[1] || "";
        const year = parts[2] || "";

        return `${day.padStart(2, "0")}.${month.padStart(2, "0")}.${year}`;
      }

      const date = new Date(dateString);
      if (isNaN(date.getTime())) return ""; // Invalid date

      return date.toLocaleDateString("de-DE", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    } catch (error) {
      console.error("Error formatting date:", error);
      return "";
    }
  };

  // Format birth date for database (yyyy-mm-dd)
  const formatBirthDateForDatabase = (dateString: string): string => {
    try {
      if (!dateString) return "";

      // If in dd.mm.yyyy format
      if (/^\d{1,2}\.\d{1,2}\.\d{4}$/.test(dateString)) {
        // Parse from dd.mm.yyyy to Date object
        const parts = dateString.split(".");
        if (parts.length !== 3) return "";

        const day = parts[0] || "";
        const month = parts[1] || "";
        const year = parts[2] || "";

        const formattedDay = day.padStart(2, "0");
        const formattedMonth = month.padStart(2, "0");

        // Create date string in yyyy-mm-dd format
        const dateStr = `${year}-${formattedMonth}-${formattedDay}`;
        const date = new Date(dateStr);

        if (isNaN(date.getTime())) return ""; // Invalid date

        // Format as yyyy-mm-dd
        const isoDate = date.toISOString();
        return isoDate.substring(0, 10); // Get yyyy-mm-dd part
      }

      // If already in a standard date format
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return ""; // Invalid date

      const isoDate = date.toISOString();
      return isoDate.substring(0, 10); // Get yyyy-mm-dd part
    } catch (error) {
      console.error("Error formatting date for database:", error);
      return "";
    }
  };

  // Auto-correct date format
  const handleBirthDateTextChange = (value: string) => {
    setBirthDateText(value);

    // Auto-correct format if user types d.m.yyyy
    if (/^\d{1,2}\.\d{1,2}\.\d{4}$/.test(value)) {
      const parts = value.split(".");
      if (parts.length !== 3) return;

      const day = parts[0] || "";
      const month = parts[1] || "";
      const year = parts[2] || "";

      const formattedDay = day.padStart(2, "0");
      const formattedMonth = month.padStart(2, "0");

      if (day !== formattedDay || month !== formattedMonth) {
        setBirthDateText(`${formattedDay}.${formattedMonth}.${year}`);
        return; // Return early to avoid validating unformatted input
      }
    }

    // Validate age when text input is complete (dd.mm.yyyy format)
    if (/^\d{2}\.\d{2}\.\d{4}$/.test(value)) {
      const validation = validateBirthDateAge(value);
      if (!validation.isValid) {
        setBirthDateValidationError(validation.message);
      } else {
        setBirthDateValidationError(null);
      }
    } else if (value === "") {
      setBirthDateValidationError(null); // Clear error when field is empty
    }
  };

  // Calculate default birth date (18 years ago from today) for date picker
  const defaultBirthDate = useMemo(() => {
    const date = new Date();
    date.setFullYear(date.getFullYear() - 18);
    return date.toISOString().split("T")[0]; // Format: YYYY-MM-DD
  }, []);

  // Initialize birth date text from customer data
  useEffect(() => {
    if (show && customer.birthdate) {
      setBirthDateText(formatBirthDateForDisplay(customer.birthdate));
    }
  }, [show, customer.birthdate]);

  // Initialize gender from customer data
  useEffect(() => {
    if (show) {
      setGender(customer.gender || null);
    }
  }, [show, customer.gender]);

  // Fetch worker data for logged-in user with lazy caching
  useEffect(() => {
    const fetchWorkerData = async () => {
      if (!user?.id) {
        console.log("No user ID available for worker data fetch");
        return;
      }

      const data = await getWorkerData(user.id);
      if (data) {
        setWorkerData(data);
      }
    };

    fetchWorkerData();
  }, [user?.id]);

  // Load contract categories on mount
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const data = await getContractCategories();
        setCategories(data);
      } catch (err) {
        setCategoryError("Fehler beim Laden der Kategorien");
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };

    if (show) {
      loadCategories();
    }
  }, [show]);

  // Load contract types when category changes
  useEffect(() => {
    const loadContractTypes = async () => {
      if (!selectedCategory) {
        setContractTypes([]);
        return;
      }

      setLoadingTypes(true);
      try {
        const rawData = await getContractTypesByCategory(selectedCategory);
        const dataWithDetails = await getDetailsForContractTypes(rawData);
        setContractTypes(dataWithDetails);
        if (restoredPageRef.current !== null) {
          setCurrentPage(restoredPageRef.current);
          restoredPageRef.current = null;
        } else {
          setCurrentPage(1); // Reset to first page when category changes
        }
      } catch (err) {
        console.error("Fehler beim Laden der Produkte:", err);
      } finally {
        setLoadingTypes(false);
      }
    };

    loadContractTypes();
  }, [selectedCategory]);

  // Restore draft once when modal opens.
  // Local persisted draft is primary; update-reload session draft remains fallback.
  useEffect(() => {
    if (!show) {
      hasAttemptedDraftRestoreRef.current = false;
      restoredPageRef.current = null;
      return;
    }

    if (hasAttemptedDraftRestoreRef.current) {
      return;
    }
    hasAttemptedDraftRestoreRef.current = true;

    let restored = false;
    const persistentDraft = getPersistentNewContractModalDraft(customer.id);
    if (persistentDraft) {
      restoredPageRef.current = persistentDraft.currentPage;
      setSelectedCategory(persistentDraft.selectedCategory);
      setSelectedContractTypes(persistentDraft.selectedContractTypes);
      setSignature(persistentDraft.signature);
      setBankDetails(persistentDraft.bankDetails);
      setBirthDateText(persistentDraft.birthDateText);
      setUseDateTextInput(persistentDraft.useDateTextInput);
      setGender(persistentDraft.gender);
      setElectricityDetails(persistentDraft.electricityDetails);
      setGasDetails(persistentDraft.gasDetails);
      setSearchQuery(persistentDraft.searchQuery);
      setCurrentPage(persistentDraft.currentPage);
      setCustomerSignatureDataUrl(persistentDraft.customerSignatureDataUrl);
      setSaveError(null);
      setBirthDateValidationError(null);
      restored = true;
    }

    if (restored || !isUpdateReloadSessionActive()) {
      return;
    }

    const draft = getNewContractModalDraft();
    if (!draft || !draft.wasOpen || draft.customerId !== customer.id) {
      return;
    }

    restoredPageRef.current = draft.currentPage;
    setSelectedCategory(draft.selectedCategory);
    setSelectedContractTypes(draft.selectedContractTypes);
    setSignature(draft.signature);
    setBankDetails(draft.bankDetails);
    setBirthDateText(draft.birthDateText);
    setUseDateTextInput(draft.useDateTextInput);
    setGender(draft.gender);
    setElectricityDetails(draft.electricityDetails);
    setGasDetails(draft.gasDetails);
    setSearchQuery(draft.searchQuery);
    setCurrentPage(draft.currentPage);
    setCustomerSignatureDataUrl(draft.customerSignatureDataUrl);
    setSaveError(null);
    setBirthDateValidationError(null);
    showNotification("Formulardaten nach Update wiederhergestellt", "info");
  }, [show, customer.id, showNotification]);

  // Persist modal draft while open for normal refresh/navigation and version-reload fallback.
  useEffect(() => {
    if (!show) {
      return;
    }

    savePersistentNewContractModalDraft(customer.id, {
      customerId: customer.id,
      selectedCategory,
      selectedContractTypes,
      signature,
      bankDetails,
      birthDateText,
      useDateTextInput,
      gender,
      electricityDetails,
      gasDetails,
      searchQuery,
      currentPage,
      customerSignatureDataUrl,
    });

    saveNewContractModalDraft({
      timestamp: Date.now(),
      customerId: customer.id,
      wasOpen: true,
      selectedCategory,
      selectedContractTypes,
      signature,
      bankDetails,
      birthDateText,
      useDateTextInput,
      gender,
      electricityDetails,
      gasDetails,
      searchQuery,
      currentPage,
      customerSignatureDataUrl,
    });
  }, [
    show,
    customer.id,
    selectedCategory,
    selectedContractTypes,
    signature,
    bankDetails,
    birthDateText,
    useDateTextInput,
    gender,
    electricityDetails,
    gasDetails,
    searchQuery,
    currentPage,
    customerSignatureDataUrl,
  ]);

  // Filter contract types based on search query
  const filteredContractTypes = useMemo(() => {
    if (!searchQuery.trim()) {
      return contractTypes;
    }

    const query = searchQuery.toLowerCase();
    return contractTypes.filter(
      (contractType) =>
        contractType.type.toLowerCase().includes(query) ||
        contractType.company_details.company.toLowerCase().includes(query) ||
        (contractType.description &&
          contractType.description.toLowerCase().includes(query))
    );
  }, [contractTypes, searchQuery]);

  // Calculate pagination
  const totalPages = Math.ceil(filteredContractTypes.length / itemsPerPage);
  const paginatedContractTypes = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredContractTypes.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredContractTypes, currentPage, itemsPerPage]);

  // Handle page change
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Clear search
  const handleClearSearch = () => {
    setSearchQuery("");
    setCurrentPage(1);
  };

  const handleSignatureSave = (signaturePath: string, dataUrl: string) => {
    console.log(
      "NewContractModal: handleSignatureSave called with path:",
      signaturePath
    );
    setSignature(signaturePath);
    setCustomerSignatureDataUrl(dataUrl);
    console.log(
      "NewContractModal: signature state updated and signature cached"
    );
    setShowSignaturePad(false);
    console.log("NewContractModal: signature pad closed");
    setSaveError(null);
  };

  const addContractType = (
    contractType: ContractType & { company_details: ContractCompany }
  ) => {
    // Check if contract type is already selected
    if (!selectedContractTypes.some((ct) => ct.id === contractType.id)) {
      setSelectedContractTypes([...selectedContractTypes, contractType]);
    }
  };

  const removeContractType = (contractTypeId: number) => {
    setSelectedContractTypes(
      selectedContractTypes.filter((ct) => ct.id !== contractTypeId)
    );
  };

  const handleSaveClick = () => {
    if (!customer.address.id || selectedContractTypes.length === 0) {
      setSaveError("Bitte wählen Sie mindestens ein Produkt aus");
      return;
    }

    // Check for date validation errors
    if (electricityDateError || gasDateError) {
      setSaveError(
        "Bitte korrigieren Sie die Fehler bei den Lieferbeginn-Daten"
      );
      return;
    }

    const missingRequiredDetail =
      getMissingRequiredUtilityDetail(selectedContractTypes);
    if (missingRequiredDetail) {
      setSaveError(
        missingRequiredDetail.detailType === "gas"
          ? `Bitte füllen Sie die Gas-Vertragsdaten für "${missingRequiredDetail.contractType}" aus.`
          : `Bitte füllen Sie die Strom-Vertragsdaten für "${missingRequiredDetail.contractType}" aus.`
      );
      return;
    }

    setShowConfirmation(true);
  };

  const handleSaveContract = async () => {
    console.log("=== handleSaveContract called ===");
    console.log("Customer:", customer);
    console.log("Selected category:", selectedCategory);
    console.log("Selected contract types:", selectedContractTypes);
    console.log("Signature:", signature);

    if (
      !customer.address.id ||
      selectedContractTypes.length === 0 ||
      !signature
    ) {
      console.error("Validation failed - missing required info");
      setSaveError("Erforderliche Informationen fehlen");
      return;
    }

    const missingRequiredDetail =
      getMissingRequiredUtilityDetail(selectedContractTypes);
    if (missingRequiredDetail) {
      setSaveError(
        missingRequiredDetail.detailType === "gas"
          ? `Bitte füllen Sie die Gas-Vertragsdaten für "${missingRequiredDetail.contractType}" aus.`
          : `Bitte füllen Sie die Strom-Vertragsdaten für "${missingRequiredDetail.contractType}" aus.`
      );
      return;
    }

    setIsSaving(true);
    setSaveError(null);

    // Get the worker ID
    const {
      data: { user },
    } = await supabase.auth.getUser();

    if (!user) {
      throw new Error("No authenticated user found");
    }

    console.log("Starting contract save...");

    try {
      let pdfGenerationFailed = false;
      let pdfGenerationError:
        | {
            code?: string;
            message: string;
            details?: string;
          }
        | null = null;
      const createdContractRefs: {
        contractTypeId: number;
        contractType: string;
        contractId: number;
      }[] = savedContractsData?.contractRefs ?? [];

      // Format birthdate correctly for database
      const formattedBirthdate = useDateTextInput
        ? formatBirthDateForDatabase(birthDateText)
        : customer.birthdate;

      // Update customer gender if changed
      if (gender !== customer.gender) {
        console.log(
          `Updating customer gender from ${customer.gender} to ${gender}`
        );
        const { error: updateError } = await supabase
          .from(TABLES.CUSTOMERS)
          .update({ gender: gender })
          .eq("id", customer.id);

        if (updateError) {
          console.error("Error updating customer gender:", updateError);
          throw new Error("Fehler beim Aktualisieren der Anrede");
        }
      }

      // Check if we already have saved data from a previous attempt
      if (savedContractsData) {
        console.log("Using previously saved contracts data");
        // Skip saving logic and proceed to PDF generation
      } else {
        // Save each selected contract
        for (const contractType of selectedContractTypes) {
          console.log("Saving contract for type:", contractType.type);
          const result = await saveContract({
            customer_id: customer.id, // Pass the existing customer ID
            firstName: customer.first_name,
            lastName: customer.last_name,
            birthdate: formattedBirthdate,
            email: customer.email,
            phone: customer.phone_number,
            bank_details: bankDetails,
            floor: "",
            notes: customer.notes,
            address_id: customer.address.id,
            contract_type_id: contractType.id,
            signature_path: signature,
          });

          // Save utility details if applicable
          if (result.contract && result.contract.id) {
            const contractId = result.contract.id;
            createdContractRefs.push({
              contractTypeId: contractType.id,
              contractType: contractType.type,
              contractId,
            });
            const requiredDetailType = getRequiredUtilityType(contractType.type);

            if (requiredDetailType === "electricity") {
              console.log(
                "Saving electricity details for contract:",
                contractId
              );
              await saveElectricityContractDetails({
                contract_id: contractId,
                previous_provider_name:
                  electricityDetails.previous_provider_name.trim(),
                previous_customer_number:
                  electricityDetails.previous_customer_number.trim(),
                annual_consumption_kwh: convertToNumber(
                  electricityDetails.annual_consumption_kwh
                ),
                meter_point_id: convertToNullableString(
                  electricityDetails.meter_point_id
                ),
                meter_number: electricityDetails.meter_number.trim(),
                meter_reading: convertToNullableNumber(
                  electricityDetails.meter_reading
                ),
                unit_price_ct_kwh: convertToNumber(
                  electricityDetails.unit_price_ct_kwh
                ),
                base_price_eur_month: convertToNumber(
                  electricityDetails.base_price_eur_month
                ),
                delivery_start_date: electricityDetails.as_soon_as_possible
                  ? null
                  : electricityDetails.delivery_start_date,
                as_soon_as_possible: electricityDetails.as_soon_as_possible,
                desired_monthly_payment: convertToNullableNumber(
                  electricityDetails.desired_monthly_payment
                ),
              });
            }

            if (requiredDetailType === "gas") {
              console.log("Saving gas details for contract:", contractId);
              await saveGasContractDetails({
                contract_id: contractId,
                previous_provider_name:
                  gasDetails.previous_provider_name.trim(),
                previous_customer_number:
                  gasDetails.previous_customer_number.trim(),
                annual_consumption_kwh: convertToNumber(
                  gasDetails.annual_consumption_kwh
                ),
                meter_point_id: convertToNullableString(
                  gasDetails.meter_point_id
                ),
                meter_number: gasDetails.meter_number.trim(),
                meter_reading: convertToNullableNumber(
                  gasDetails.meter_reading
                ),
                unit_price_ct_kwh: convertToNumber(
                  gasDetails.unit_price_ct_kwh
                ),
                base_price_eur_month: convertToNumber(
                  gasDetails.base_price_eur_month
                ),
                delivery_start_date: gasDetails.as_soon_as_possible
                  ? null
                  : gasDetails.delivery_start_date,
                as_soon_as_possible: gasDetails.as_soon_as_possible,
                desired_monthly_payment: convertToNullableNumber(
                  gasDetails.desired_monthly_payment
                ),
              });
            }
          }
        }

        // Cache the successful result
        setSavedContractsData({
          success: true,
          contractRefs: createdContractRefs,
        });
      }

      console.log("All contracts saved successfully");

      // Show success notification for customer saved
      showNotification("Kunde gespeichert", "success");

      // Check if PDF generation and email sending is required
      // Only for Stadtwerke Dessau category with Gas or Strom contracts
      const hasGasOrStromContract = selectedContractTypes.some(
        (ct) =>
          isGasContract(ct.type) || ct.type.toLowerCase().includes("strom")
      );

      const shouldGeneratePDF =
        isStadtwerkeDessauCategory(selectedCategory) && hasGasOrStromContract;

      console.log("Selected category:", selectedCategory);
      console.log("Has Gas/Strom contract:", hasGasOrStromContract);
      console.log("Should generate PDF:", shouldGeneratePDF);

      // Generate PDFs for each contract type that needs it
      if (shouldGeneratePDF) {
        console.log("Starting PDF generation and email flow...");

        // Ensure worker data (signature) is loaded before generating PDF
        // This fixes the race condition where signature might be missing
        let currentWorkerData = workerData;
        if (user?.id) {
          console.log(
            "Ensuring worker data is loaded before PDF generation..."
          );
          const freshWorkerData = await getWorkerData(user.id, true);
          if (freshWorkerData) {
            currentWorkerData = freshWorkerData;
            setWorkerData(freshWorkerData); // Update state as well
            console.log("Worker data refreshed/loaded for PDF generation");
          }
        }

        // Use cached customer signature or download from storage for PDF generation
        let customerSignatureBase64: string | undefined;
        if (customerSignatureDataUrl) {
          // Use cached signature (from current session)
          customerSignatureBase64 = customerSignatureDataUrl;
          console.log("Using cached customer signature for PDF");
        } else if (signature) {
          // Fallback: download from storage if not cached (e.g., after page refresh)
          try {
            customerSignatureBase64 = await downloadSignatureAsBase64(
              signature
            );
            console.log(
              "Customer signature downloaded from storage (cache miss)"
            );
          } catch (error) {
            console.error("Error downloading customer signature:", error);
            // Continue without signature rather than failing entirely
          }
        }

        // Filter only Gas/Strom contracts for PDF generation
        const gasStromContracts = selectedContractTypes.filter(
          (ct) =>
            isGasContract(ct.type) || ct.type.toLowerCase().includes("strom")
        );

        // Step 1: Generate all PDFs first
        let workerIdForMetadata: number | undefined;
        if (user?.id) {
          try {
            workerIdForMetadata = await getWorkerIdForUser(user.id);
          } catch (workerIdError) {
            console.warn("Could not resolve worker id for email metadata", {
              error: workerIdError,
            });
          }
        }

        const generatedPdfs: Array<{
          blob: Blob;
          contractType: string;
          metadata: {
            source: "contract_api";
            contractId?: number;
            customerId: number;
            contractType: string;
            createdByWorkerId?: number;
            createdByUserId?: string;
          };
        }> = [];

        for (const contractType of gasStromContracts) {
          try {
            console.log(
              `Generating PDF for contract type: ${contractType.type}`
            );

            const { templatePath } = determinePdfTemplate(contractType.type);
            console.log("PDF template selected:", templatePath);

            // Debug: Log employee signature status
            console.log("🔍 Employee signature status:", {
              hasWorkerData: !!currentWorkerData,
              hasSignature: !!currentWorkerData?.signature_base64,
              signatureLength: currentWorkerData?.signature_base64?.length,
              signaturePreview:
                currentWorkerData?.signature_base64?.substring(0, 50) + "...",
            });

            // Prepare PDF data for this specific contract type
            const pdfData: ContractPdfData = {
              customer: {
                firstName: customer.first_name,
                lastName: customer.last_name,
                birthdate: useDateTextInput
                  ? birthDateText
                  : formatBirthDateForDisplay(customer.birthdate),
                email: customer.email,
                phone: customer.phone_number,
                address: {
                  street: customer.address.street,
                  houseNumber: customer.address.house_number,
                  zipCode: customer.address.zip_code,
                  city: customer.address.city,
                  country: customer.address.country,
                },
              },
              bankDetails: bankDetails,
              signatureBase64: customerSignatureBase64,
              contractType: contractType.type,
              selectedDate: getCurrentDateFormatted(),
              // Optional general fields
              salesPartnerCompany: currentWorkerData?.organization || undefined,
              salesEmployeeName: currentWorkerData?.full_name || undefined,
              employeeSignatureBase64:
                currentWorkerData?.signature_base64 || undefined,
              invoiceRecipient: undefined, // TODO: Add form field if needed
              invoiceStreet: undefined, // TODO: Add form field if needed
              invoiceZipCity: undefined, // TODO: Add form field if needed
              signatureLocation: customer.address.city || undefined,
              // Utility-specific details
              gasDetails: isGasContract(contractType.type)
                ? {
                    previous_provider_name: gasDetails.previous_provider_name,
                    previous_customer_number:
                      gasDetails.previous_customer_number,
                    annual_consumption_kwh: gasDetails.annual_consumption_kwh,
                    meter_point_id: gasDetails.meter_point_id || undefined,
                    meter_number: gasDetails.meter_number,
                    meter_reading: gasDetails.meter_reading || undefined,
                    unit_price_ct_kwh: gasDetails.unit_price_ct_kwh,
                    base_price_eur_month: gasDetails.base_price_eur_month,
                    delivery_start_date: gasDetails.as_soon_as_possible
                      ? "schellst."
                      : gasDetails.delivery_start_date,
                    desired_monthly_payment:
                      gasDetails.desired_monthly_payment || undefined,
                  }
                : undefined,
              electricityDetails: contractType.type
                .toLowerCase()
                .includes("strom")
                ? {
                    previous_provider_name:
                      electricityDetails.previous_provider_name,
                    previous_customer_number:
                      electricityDetails.previous_customer_number,
                    annual_consumption_kwh:
                      electricityDetails.annual_consumption_kwh,
                    meter_point_id:
                      electricityDetails.meter_point_id || undefined,
                    meter_number: electricityDetails.meter_number,
                    meter_reading:
                      electricityDetails.meter_reading || undefined,
                    unit_price_ct_kwh: electricityDetails.unit_price_ct_kwh,
                    base_price_eur_month:
                      electricityDetails.base_price_eur_month,
                    delivery_start_date: electricityDetails.as_soon_as_possible
                      ? "schellst."
                      : electricityDetails.delivery_start_date,
                    desired_monthly_payment:
                      electricityDetails.desired_monthly_payment || undefined,
                  }
                : undefined,
            };

            // Generate PDF
            console.log(
              `Generating PDF for ${contractType.type} with data:`,
              pdfData
            );
            const pdfResult = await generateContractPdf(pdfData, templatePath);
            console.log(
              `PDF generation result for ${contractType.type}:`,
              pdfResult
            );

            if (!pdfResult.success || !pdfResult.pdfBlob) {
              pdfGenerationFailed = true;
              pdfGenerationError = {
                code: pdfResult.errorCode,
                message: pdfResult.error || "PDF generation failed",
                details: pdfResult.errorDetails,
              };
              console.error(
                `PDF generation blocked for ${contractType.type}:`,
                pdfGenerationError
              );
              break;
            }

            console.log(
              `✓ PDF generated successfully for ${contractType.type}`
            );
            const matchedContractRef = createdContractRefs.find(
              (item) => item.contractTypeId === contractType.id
            );
            generatedPdfs.push({
              blob: pdfResult.pdfBlob,
              contractType: contractType.type,
              metadata: {
                source: "contract_api",
                contractId: matchedContractRef?.contractId,
                customerId: customer.id,
                contractType: contractType.type,
                createdByWorkerId: workerIdForMetadata,
                createdByUserId: user.id,
              },
            });
          } catch (pdfError) {
            console.error(
              `PDF generation error for ${contractType.type}:`,
              pdfError
            );
            pdfGenerationFailed = true;
            pdfGenerationError = {
              message:
                pdfError instanceof Error
                  ? pdfError.message
                  : "PDF generation failed",
            };
            break; // Stop generating more PDFs if one fails
          }
        }

        // Step 2: Send one email per contract type (management jobs first, then customer jobs)
        if (!pdfGenerationFailed && generatedPdfs.length > 0) {
          console.log(`Sending ${generatedPdfs.length} PDF(s) via email...`);

          const emailResult =
            await sendContractEmailBatchWithManagementPriority(
              generatedPdfs,
              customer.email,
              `${customer.first_name} ${customer.last_name}`,
              customer.last_name
            );

          if (emailResult.managementEmailSent) {
            showNotification(
              "Mail an verwaltung@teleconnect24.de gesendet",
              "success"
            );
          }
          if (
            !emailResult.customerEmailMissing &&
            emailResult.customerEmailSent
          ) {
            showNotification(`Mail an ${customer.email} gesendet`, "success");
          }

          // Handle email result
          let summaryMessage = "";
          const contractTypes = generatedPdfs
            .map((p) => p.contractType)
            .join(" und ");
          const managementSentCount =
            emailResult.managementSentCount ??
            (emailResult.managementEmailSent ? generatedPdfs.length : 0);
          const customerTargetCount = emailResult.customerEmailMissing
            ? 0
            : generatedPdfs.length;
          const customerSentCount =
            emailResult.customerSentCount ??
            (emailResult.customerEmailSent ? customerTargetCount : 0);
          const failedJobsCount = emailResult.failedJobs?.length ?? 0;

          if (emailResult.customerEmailMissing) {
            summaryMessage = `Verträge (${contractTypes}) gespeichert. Backoffice-E-Mails: ${managementSentCount}/${generatedPdfs.length}. Kunde hat keine E-Mail-Adresse, PDFs wurden lokal gespeichert.`;
            setSuccessModalSuccess(
              managementSentCount === generatedPdfs.length
            );
            setSuccessModalActionLabel(undefined);
          } else if (emailResult.success && !emailResult.downloadTriggered) {
            summaryMessage =
              generatedPdfs.length === 1
                ? `Vertrag (${contractTypes}) erfolgreich gespeichert und per E-Mail versendet!`
                : `Alle ${generatedPdfs.length} Verträge (${contractTypes}) erfolgreich gespeichert und per E-Mail versendet!`;
            setSuccessModalSuccess(true);
            setSuccessModalActionLabel(undefined);
          } else if (emailResult.downloadTriggered) {
            summaryMessage = `Verträge (${contractTypes}) gespeichert. Backoffice-E-Mails: ${managementSentCount}/${generatedPdfs.length}, Kunden-E-Mails: ${customerSentCount}/${customerTargetCount}. Fehlgeschlagene Sendungen: ${failedJobsCount}. PDFs wurden lokal gespeichert.`;
            setSuccessModalSuccess(false);
            setSuccessModalActionLabel(undefined);
          } else {
            summaryMessage = `Verträge (${contractTypes}) gespeichert. Backoffice-E-Mails: ${managementSentCount}/${generatedPdfs.length}, Kunden-E-Mails: ${customerSentCount}/${customerTargetCount}. Fehlgeschlagene Sendungen: ${failedJobsCount}.`;
            setSuccessModalSuccess(false);
            setSuccessModalActionLabel(undefined);
          }
          setSuccessModalMessage(summaryMessage);
        } else {
          const isPdfIntegrityFailure =
            pdfGenerationError?.code === "PDF_TEMPLATE_INTEGRITY_FAILED" ||
            pdfGenerationError?.code === "PDF_FILLED_OUTPUT_INVALID" ||
            pdfGenerationError?.code === "PDF_FIELD_APPEARANCE_FAILED" ||
            pdfGenerationError?.code === "PDF_FLATTEN_FAILED";

          const summaryMessage = isPdfIntegrityFailure
            ? `Verträge wurden gespeichert, aber der E-Mail-Versand wurde wegen eines PDF-Integritätsfehlers blockiert (${pdfGenerationError?.code ?? "UNKNOWN"}). Bitte informieren Sie die Verwaltung, damit der Versand manuell geprüft und ausgelöst wird.`
            : "Verträge gespeichert, aber PDF-Generierung fehlgeschlagen.";
          setSuccessModalSuccess(false);
          setSuccessModalMessage(summaryMessage);
          setSuccessModalActionLabel(
            "Als gespeichert ohne E-Mail schließen"
          );
        }

        setShowSuccessModal(true);
      } else {
        // No PDF needed, show standard success
        console.log(
          "PDF generation skipped - category does not match energy categories"
        );
        setShowSuccessAlert(true);
      }

      // Wait a moment to show success message before closing
      if (!pdfGenerationFailed) {
        clearPersistentNewContractModalDraft(customer.id);
        setTimeout(
          () => {
            onSuccess();
            handleClose();
          },
          shouldGeneratePDF ? 500 : 1000
        );
      }
    } catch (error) {
      console.error("Error in handleSaveContract:", error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Fehler beim Speichern des Kunden";
      const isUtilityDetailsError =
        /electricity details|gas details|utility details|strom|gas/i.test(
          errorMessage
        );
      setSaveError(
        isUtilityDetailsError
          ? `Vertrag wurde eventuell teilweise gespeichert, aber Energiedaten konnten nicht vollständig gespeichert werden: ${errorMessage}`
          : errorMessage
      );
    } finally {
      console.log("handleSaveContract finished, setIsSaving(false)");
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    setSelectedCategory("");
    setSelectedContractTypes([]);
    setSignature(null);
    setCustomerSignatureDataUrl(null);
    setShowSignaturePad(false);
    setShowConfirmation(false);
    setSaveError(null);
    setBankDetails("");
    setSearchQuery("");
    setCurrentPage(1);
    setGender(null); // Reset gender
    setBirthDateValidationError(null); // Reset validation error
    // Reset electricity and gas details
    setElectricityDetails(createEmptyUtilityContractDetails());
    setGasDetails(createEmptyUtilityContractDetails());
    setSavedContractsData(null); // Reset saved contracts data
    clearPersistentNewContractModalDraft(customer.id);
    clearNewContractModalDraft();
    clearNewContractModalOpenState();
    onClose();
  };

  if (!show) return null;

  return (
    <>
      {/* Custom Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto overflow-x-hidden bg-gray-900 bg-opacity-50">
        <div className="relative max-h-[90vh] w-[90vw] max-w-7xl overflow-y-auto rounded-lg bg-white p-6 shadow dark:bg-gray-800">
          {/* Modal Header */}
          <div className="mb-4 flex items-center justify-between border-b pb-4">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
              Neues Produkt hinzufügen
            </h3>
            <button
              onClick={handleClose}
              className="ml-auto inline-flex items-center rounded-lg bg-transparent p-1.5 text-sm text-gray-400 hover:bg-gray-200 hover:text-gray-900 dark:hover:bg-gray-600 dark:hover:text-white"
              type="button"
              aria-label="Schließen"
            >
              <svg
                className="h-5 w-5"
                fill="currentColor"
                viewBox="0 0 20 20"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clipRule="evenodd"
                ></path>
              </svg>
            </button>
          </div>

          {/* Modal Body */}
          <div className="space-y-6">
            {saveError && (
              <Alert color="failure" onDismiss={() => setSaveError(null)}>
                {saveError}
              </Alert>
            )}
            {showSuccessAlert && (
              <Alert color="success">Kunde erfolgreich gespeichert</Alert>
            )}

            {/* Gender Selection */}
            <div>
              <label
                htmlFor="gender"
                className="mb-2 block text-sm font-medium text-gray-900 dark:text-white"
              >
                Anrede
              </label>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setGender(1)}
                  className={`rounded-lg px-4 py-2 text-sm font-medium ${
                    gender === 1
                      ? "bg-primary-700 text-white"
                      : "bg-gray-100 text-gray-900 hover:bg-gray-200 dark:bg-gray-700 dark:text-white dark:hover:bg-gray-600"
                  }`}
                >
                  Herr
                </button>
                <button
                  type="button"
                  onClick={() => setGender(2)}
                  className={`rounded-lg px-4 py-2 text-sm font-medium ${
                    gender === 2
                      ? "bg-primary-700 text-white"
                      : "bg-gray-100 text-gray-900 hover:bg-gray-200 dark:bg-gray-700 dark:text-white dark:hover:bg-gray-600"
                  }`}
                >
                  Frau
                </button>
                <button
                  type="button"
                  onClick={() => setGender(3)}
                  className={`rounded-lg px-4 py-2 text-sm font-medium ${
                    gender === 3
                      ? "bg-primary-700 text-white"
                      : "bg-gray-100 text-gray-900 hover:bg-gray-200 dark:bg-gray-700 dark:text-white dark:hover:bg-gray-600"
                  }`}
                >
                  Divers
                </button>
              </div>
            </div>

            {/* Birth Date Input */}
            <div>
              <div className="mb-2 flex items-center justify-between">
                <label
                  htmlFor="birthdate"
                  className="block text-sm font-medium text-gray-900 dark:text-white"
                >
                  Geburtsdatum
                </label>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    Datumseingabe
                  </span>
                  <ToggleSwitch
                    checked={useDateTextInput}
                    onChange={handleToggleChange}
                    label=""
                  />
                </div>
              </div>

              {useDateTextInput ? (
                <TextInput
                  id="birthdate_text"
                  type="text"
                  value={birthDateText}
                  onChange={(e) => handleBirthDateTextChange(e.target.value)}
                  className="block w-full"
                  placeholder="TT.MM.JJJJ"
                />
              ) : (
                <input
                  type="date"
                  id="birthdate"
                  value={customer.birthdate || defaultBirthDate}
                  max={defaultBirthDate}
                  onChange={(e) => {
                    // When using the date picker, update the customer birthdate value directly
                    customer.birthdate = e.target.value;

                    // Validate age when date is selected
                    if (e.target.value) {
                      const validation = validateBirthDateAge(e.target.value);
                      if (!validation.isValid) {
                        setBirthDateValidationError(validation.message);
                      } else {
                        setBirthDateValidationError(null);
                      }
                    } else {
                      setBirthDateValidationError(null);
                    }
                  }}
                  className="block w-full rounded-lg border border-gray-300 bg-gray-50 p-2.5 text-sm text-gray-900 focus:border-primary-500 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder:text-gray-400 dark:focus:border-primary-500 dark:focus:ring-primary-500"
                />
              )}
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Format: TT.MM.JJJJ
              </p>
              {/* Birth Date Validation Error */}
              {birthDateValidationError && (
                <div className="mt-2 rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
                  {birthDateValidationError}
                </div>
              )}
            </div>

            {/* Bank Details */}
            <div>
              <label
                htmlFor="bank_details"
                className="mb-2 block text-sm font-medium text-gray-900 dark:text-white"
              >
                Verbindung
              </label>
              <input
                type="text"
                id="bank_details"
                value={
                  bankDetailsFocused
                    ? bankDetails
                    : (bankDetails || "").replace(/./g, "•")
                }
                onChange={(e) => setBankDetails(e.target.value)}
                onFocus={() => setBankDetailsFocused(true)}
                onBlur={() => setBankDetailsFocused(false)}
                autoComplete="off"
                className="block w-full rounded-lg border border-gray-300 bg-gray-50 p-2.5 text-sm text-gray-900 focus:border-primary-500 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder:text-gray-400 dark:focus:border-primary-500 dark:focus:ring-primary-500"
                placeholder="IBAN oder Kontonummer"
              />
            </div>

            {/* Contract Category */}
            <div>
              <label
                htmlFor="category"
                className="mb-2 block text-sm font-medium text-gray-900 dark:text-white"
              >
                Kategorie
              </label>
              <Select
                id="category"
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full"
                disabled={isLoading}
              >
                <option value="">Kategorie auswählen</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.category}>
                    {category.category}
                  </option>
                ))}
              </Select>
              {categoryError && (
                <p className="mt-2 text-sm text-red-600 dark:text-red-500">
                  {categoryError}
                </p>
              )}
            </div>

            {/* Electricity (Strom) Details - Show only if any selected contract type contains "Strom" */}
            {selectedContractTypes.some((ct) =>
              ct.type.toLowerCase().includes("strom")
            ) && (
              <div className="space-y-4 rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-800 dark:bg-blue-900/20">
                <h4 className="text-base font-semibold text-gray-900 dark:text-white">
                  Stromvertragsdetails{" "}
                  {!isStadtwerkeDessau && (
                    <span className="text-sm font-normal text-gray-500">
                      (Optional)
                    </span>
                  )}
                </h4>

                <div className="grid gap-4 md:grid-cols-2">
                  {/* Previous Provider Name */}
                  <div>
                    <label
                      htmlFor="elec_prev_provider"
                      className="mb-2 block text-sm font-medium text-gray-900 dark:text-white"
                    >
                      Name des bisherigen Stromlieferanten{" "}
                      {isStadtwerkeDessau && "*"}
                    </label>
                    <TextInput
                      id="elec_prev_provider"
                      type="text"
                      value={electricityDetails.previous_provider_name}
                      onChange={(e) =>
                        setElectricityDetails({
                          ...electricityDetails,
                          previous_provider_name: e.target.value,
                        })
                      }
                      required={isStadtwerkeDessau}
                      placeholder="z.B. Vattenfall"
                    />
                  </div>

                  {/* Previous Customer Number */}
                  <div>
                    <label
                      htmlFor="elec_prev_customer_num"
                      className="mb-2 block text-sm font-medium text-gray-900 dark:text-white"
                    >
                      Kundennummer beim bisherigen Stromlieferanten{" "}
                      {isStadtwerkeDessau && "*"}
                    </label>
                    <TextInput
                      id="elec_prev_customer_num"
                      type="text"
                      value={electricityDetails.previous_customer_number}
                      onChange={(e) =>
                        setElectricityDetails({
                          ...electricityDetails,
                          previous_customer_number: e.target.value,
                        })
                      }
                      required={isStadtwerkeDessau}
                      placeholder="Kundennummer"
                    />
                  </div>

                  {/* Annual Consumption */}
                  <div>
                    <label
                      htmlFor="elec_annual_consumption"
                      className="mb-2 block text-sm font-medium text-gray-900 dark:text-white"
                    >
                      Vorjahresstromverbrauch in kWh {isStadtwerkeDessau && "*"}
                    </label>
                    <TextInput
                      id="elec_annual_consumption"
                      type="number"
                      value={electricityDetails.annual_consumption_kwh}
                      onChange={(e) =>
                        setElectricityDetails({
                          ...electricityDetails,
                          annual_consumption_kwh: e.target.value,
                        })
                      }
                      required={isStadtwerkeDessau}
                      placeholder="z.B. 3500"
                    />
                  </div>

                  {/* Meter Point ID */}
                  <div>
                    <label
                      htmlFor="elec_meter_point_id"
                      className="mb-2 block text-sm font-medium text-gray-900 dark:text-white"
                    >
                      Zählpunktbezeichnung / ID der Marktlokation
                    </label>
                    <TextInput
                      id="elec_meter_point_id"
                      type="text"
                      value={electricityDetails.meter_point_id}
                      onChange={(e) =>
                        setElectricityDetails({
                          ...electricityDetails,
                          meter_point_id: e.target.value,
                        })
                      }
                      placeholder="Optional"
                    />
                  </div>

                  {/* Meter Number */}
                  <div>
                    <label
                      htmlFor="elec_meter_number"
                      className="mb-2 block text-sm font-medium text-gray-900 dark:text-white"
                    >
                      Eigentumsnummer des Stromzählers{" "}
                      {isStadtwerkeDessau && "*"}
                    </label>
                    <TextInput
                      id="elec_meter_number"
                      type="text"
                      value={electricityDetails.meter_number}
                      onChange={(e) =>
                        setElectricityDetails({
                          ...electricityDetails,
                          meter_number: e.target.value,
                        })
                      }
                      required={isStadtwerkeDessau}
                      placeholder="Zählernummer"
                    />
                  </div>

                  {/* Meter Reading */}
                  <div>
                    <label
                      htmlFor="elec_meter_reading"
                      className="mb-2 block text-sm font-medium text-gray-900 dark:text-white"
                    >
                      Zählerstand
                    </label>
                    <TextInput
                      id="elec_meter_reading"
                      type="number"
                      value={electricityDetails.meter_reading}
                      onChange={(e) =>
                        setElectricityDetails({
                          ...electricityDetails,
                          meter_reading: e.target.value,
                        })
                      }
                      placeholder="Optional"
                    />
                  </div>

                  {/* Unit Price */}
                  <div>
                    <label
                      htmlFor="elec_unit_price"
                      className="mb-2 block text-sm font-medium text-gray-900 dark:text-white"
                    >
                      Arbeitspreis ct/kWh/brutto {isStadtwerkeDessau && "*"}
                    </label>
                    <TextInput
                      id="elec_unit_price"
                      type="number"
                      step="0.01"
                      value={electricityDetails.unit_price_ct_kwh}
                      onChange={(e) =>
                        setElectricityDetails({
                          ...electricityDetails,
                          unit_price_ct_kwh: e.target.value,
                        })
                      }
                      required={isStadtwerkeDessau}
                      placeholder="z.B. 32.50"
                    />
                  </div>

                  {/* Base Price */}
                  <div>
                    <label
                      htmlFor="elec_base_price"
                      className="mb-2 block text-sm font-medium text-gray-900 dark:text-white"
                    >
                      Grundpreis EUR/Monat/brutto {isStadtwerkeDessau && "*"}
                    </label>
                    <TextInput
                      id="elec_base_price"
                      type="number"
                      step="0.01"
                      value={electricityDetails.base_price_eur_month}
                      onChange={(e) =>
                        setElectricityDetails({
                          ...electricityDetails,
                          base_price_eur_month: e.target.value,
                        })
                      }
                      required={isStadtwerkeDessau}
                      placeholder="z.B. 12.99"
                    />
                  </div>

                  {/* Delivery Start Date */}
                  <div>
                    <label
                      htmlFor="elec_delivery_start"
                      className="mb-2 block text-sm font-medium text-gray-900 dark:text-white"
                    >
                      Nächstmöglicher Zeitpunkt Lieferbeginn{" "}
                      {isStadtwerkeDessau && "*"}
                    </label>

                    {/* Checkbox for "as soon as possible" */}
                    <div className="mb-2">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={electricityDetails.as_soon_as_possible}
                          onChange={(e) => {
                            setElectricityDetails({
                              ...electricityDetails,
                              as_soon_as_possible: e.target.checked,
                              delivery_start_date: e.target.checked
                                ? ""
                                : electricityDetails.delivery_start_date,
                            });
                            // Clear date error when checkbox is checked
                            if (e.target.checked) {
                              setElectricityDateError(null);
                            }
                          }}
                          className="mr-2 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">
                          Schnellstmöglich (kein spezifisches Datum)
                        </span>
                      </label>
                    </div>

                    {/* Date input field */}
                    <input
                      id="elec_delivery_start"
                      type="date"
                      value={electricityDetails.delivery_start_date}
                      onChange={(e) => {
                        const newDate = e.target.value;
                        setElectricityDetails({
                          ...electricityDetails,
                          delivery_start_date: newDate,
                        });

                        // Validate date
                        if (newDate && !validateDeliveryDate(newDate)) {
                          setElectricityDateError(
                            "Das Lieferbeginn-Datum muss nach dem heutigen Datum liegen"
                          );
                        } else {
                          setElectricityDateError(null);
                        }
                      }}
                      required={
                        isStadtwerkeDessau &&
                        !electricityDetails.as_soon_as_possible
                      }
                      disabled={electricityDetails.as_soon_as_possible}
                      className={`block w-full rounded-lg border p-2.5 text-sm ${
                        electricityDetails.as_soon_as_possible
                          ? "cursor-not-allowed bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-500"
                          : electricityDateError
                          ? "border-red-500 bg-red-50 text-red-900 focus:border-red-500 focus:ring-red-500 dark:border-red-400 dark:bg-red-900/20 dark:text-red-400"
                          : "border-gray-300 bg-gray-50 text-gray-900 focus:border-primary-500 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                      } dark:placeholder:text-gray-400 dark:focus:border-primary-500 dark:focus:ring-primary-500`}
                    />
                    {electricityDateError && (
                      <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                        {electricityDateError}
                      </p>
                    )}

                    {/* Show "schellst." indicator when checkbox is checked */}
                    {electricityDetails.as_soon_as_possible && (
                      <div className="mt-2 rounded-md bg-blue-50 p-2 text-sm text-blue-700 dark:bg-blue-900/20 dark:text-blue-300">
                        ✓ Lieferbeginn: Schnellstmöglich
                      </div>
                    )}
                  </div>

                  {/* Desired Monthly Payment */}
                  <div>
                    <label
                      htmlFor="elec_monthly_payment"
                      className="mb-2 block text-sm font-medium text-gray-900 dark:text-white"
                    >
                      gewünschter monatl. Abschlag
                    </label>
                    <TextInput
                      id="elec_monthly_payment"
                      type="number"
                      step="0.01"
                      value={electricityDetails.desired_monthly_payment}
                      onChange={(e) =>
                        setElectricityDetails({
                          ...electricityDetails,
                          desired_monthly_payment: e.target.value,
                        })
                      }
                      placeholder="Optional"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Gas Details - Show only if any selected contract type is a gas contract */}
            {selectedContractTypes.some((ct) => isGasContract(ct.type)) && (
              <div className="space-y-4 rounded-lg border border-orange-200 bg-orange-50 p-4 dark:border-orange-800 dark:bg-orange-900/20">
                <h4 className="text-base font-semibold text-gray-900 dark:text-white">
                  Gasvertragsdetails{" "}
                  {!isStadtwerkeDessau && (
                    <span className="text-sm font-normal text-gray-500">
                      (Optional)
                    </span>
                  )}
                </h4>

                <div className="grid gap-4 md:grid-cols-2">
                  {/* Previous Provider Name */}
                  <div>
                    <label
                      htmlFor="gas_prev_provider"
                      className="mb-2 block text-sm font-medium text-gray-900 dark:text-white"
                    >
                      Name des bisherigen Gaslieferanten{" "}
                      {isStadtwerkeDessau && "*"}
                    </label>
                    <TextInput
                      id="gas_prev_provider"
                      type="text"
                      value={gasDetails.previous_provider_name}
                      onChange={(e) =>
                        setGasDetails({
                          ...gasDetails,
                          previous_provider_name: e.target.value,
                        })
                      }
                      required={isStadtwerkeDessau}
                      placeholder="z.B. Gasag"
                    />
                  </div>

                  {/* Previous Customer Number */}
                  <div>
                    <label
                      htmlFor="gas_prev_customer_num"
                      className="mb-2 block text-sm font-medium text-gray-900 dark:text-white"
                    >
                      Kundennummer beim bisherigen Gaslieferanten{" "}
                      {isStadtwerkeDessau && "*"}
                    </label>
                    <TextInput
                      id="gas_prev_customer_num"
                      type="text"
                      value={gasDetails.previous_customer_number}
                      onChange={(e) =>
                        setGasDetails({
                          ...gasDetails,
                          previous_customer_number: e.target.value,
                        })
                      }
                      required={isStadtwerkeDessau}
                      placeholder="Kundennummer"
                    />
                  </div>

                  {/* Annual Consumption */}
                  <div>
                    <label
                      htmlFor="gas_annual_consumption"
                      className="mb-2 block text-sm font-medium text-gray-900 dark:text-white"
                    >
                      Vorjahresgasverbrauch in kWh {isStadtwerkeDessau && "*"}
                    </label>
                    <TextInput
                      id="gas_annual_consumption"
                      type="number"
                      value={gasDetails.annual_consumption_kwh}
                      onChange={(e) =>
                        setGasDetails({
                          ...gasDetails,
                          annual_consumption_kwh: e.target.value,
                        })
                      }
                      required={isStadtwerkeDessau}
                      placeholder="z.B. 15000"
                    />
                  </div>

                  {/* Meter Point ID */}
                  <div>
                    <label
                      htmlFor="gas_meter_point_id"
                      className="mb-2 block text-sm font-medium text-gray-900 dark:text-white"
                    >
                      Zählpunktbezeichnung / ID der Marktlokation
                    </label>
                    <TextInput
                      id="gas_meter_point_id"
                      type="text"
                      value={gasDetails.meter_point_id}
                      onChange={(e) =>
                        setGasDetails({
                          ...gasDetails,
                          meter_point_id: e.target.value,
                        })
                      }
                      placeholder="Optional"
                    />
                  </div>

                  {/* Meter Number */}
                  <div>
                    <label
                      htmlFor="gas_meter_number"
                      className="mb-2 block text-sm font-medium text-gray-900 dark:text-white"
                    >
                      Eigentumsnummer des Gaszählers {isStadtwerkeDessau && "*"}
                    </label>
                    <TextInput
                      id="gas_meter_number"
                      type="text"
                      value={gasDetails.meter_number}
                      onChange={(e) =>
                        setGasDetails({
                          ...gasDetails,
                          meter_number: e.target.value,
                        })
                      }
                      required={isStadtwerkeDessau}
                      placeholder="Zählernummer"
                    />
                  </div>

                  {/* Meter Reading */}
                  <div>
                    <label
                      htmlFor="gas_meter_reading"
                      className="mb-2 block text-sm font-medium text-gray-900 dark:text-white"
                    >
                      Zählerstand
                    </label>
                    <TextInput
                      id="gas_meter_reading"
                      type="number"
                      value={gasDetails.meter_reading}
                      onChange={(e) =>
                        setGasDetails({
                          ...gasDetails,
                          meter_reading: e.target.value,
                        })
                      }
                      placeholder="Optional"
                    />
                  </div>

                  {/* Unit Price */}
                  <div>
                    <label
                      htmlFor="gas_unit_price"
                      className="mb-2 block text-sm font-medium text-gray-900 dark:text-white"
                    >
                      Arbeitspreis ct/kWh/brutto {isStadtwerkeDessau && "*"}
                    </label>
                    <TextInput
                      id="gas_unit_price"
                      type="number"
                      step="0.01"
                      value={gasDetails.unit_price_ct_kwh}
                      onChange={(e) =>
                        setGasDetails({
                          ...gasDetails,
                          unit_price_ct_kwh: e.target.value,
                        })
                      }
                      required={isStadtwerkeDessau}
                      placeholder="z.B. 12.50"
                    />
                  </div>

                  {/* Base Price */}
                  <div>
                    <label
                      htmlFor="gas_base_price"
                      className="mb-2 block text-sm font-medium text-gray-900 dark:text-white"
                    >
                      Grundpreis EUR/Monat/brutto {isStadtwerkeDessau && "*"}
                    </label>
                    <TextInput
                      id="gas_base_price"
                      type="number"
                      step="0.01"
                      value={gasDetails.base_price_eur_month}
                      onChange={(e) =>
                        setGasDetails({
                          ...gasDetails,
                          base_price_eur_month: e.target.value,
                        })
                      }
                      required={isStadtwerkeDessau}
                      placeholder="z.B. 9.99"
                    />
                  </div>

                  {/* Delivery Start Date */}
                  <div>
                    <label
                      htmlFor="gas_delivery_start"
                      className="mb-2 block text-sm font-medium text-gray-900 dark:text-white"
                    >
                      Nächstmöglicher Zeitpunkt Lieferbeginn{" "}
                      {isStadtwerkeDessau && "*"}
                    </label>

                    {/* Checkbox for "as soon as possible" */}
                    <div className="mb-2">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={gasDetails.as_soon_as_possible}
                          onChange={(e) => {
                            setGasDetails({
                              ...gasDetails,
                              as_soon_as_possible: e.target.checked,
                              delivery_start_date: e.target.checked
                                ? ""
                                : gasDetails.delivery_start_date,
                            });
                            // Clear date error when checkbox is checked
                            if (e.target.checked) {
                              setGasDateError(null);
                            }
                          }}
                          className="mr-2 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">
                          Schnellstmöglich (kein spezifisches Datum)
                        </span>
                      </label>
                    </div>

                    {/* Date input field */}
                    <input
                      id="gas_delivery_start"
                      type="date"
                      value={gasDetails.delivery_start_date}
                      onChange={(e) => {
                        const newDate = e.target.value;
                        setGasDetails({
                          ...gasDetails,
                          delivery_start_date: newDate,
                        });

                        // Validate date
                        if (newDate && !validateDeliveryDate(newDate)) {
                          setGasDateError(
                            "Das Lieferbeginn-Datum muss nach dem heutigen Datum liegen"
                          );
                        } else {
                          setGasDateError(null);
                        }
                      }}
                      required={
                        isStadtwerkeDessau && !gasDetails.as_soon_as_possible
                      }
                      disabled={gasDetails.as_soon_as_possible}
                      className={`block w-full rounded-lg border p-2.5 text-sm ${
                        gasDetails.as_soon_as_possible
                          ? "cursor-not-allowed bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-500"
                          : gasDateError
                          ? "border-red-500 bg-red-50 text-red-900 focus:border-red-500 focus:ring-red-500 dark:border-red-400 dark:bg-red-900/20 dark:text-red-400"
                          : "border-gray-300 bg-gray-50 text-gray-900 focus:border-primary-500 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                      } dark:placeholder:text-gray-400 dark:focus:border-primary-500 dark:focus:ring-primary-500`}
                    />
                    {gasDateError && (
                      <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                        {gasDateError}
                      </p>
                    )}

                    {/* Show "schellst." indicator when checkbox is checked */}
                    {gasDetails.as_soon_as_possible && (
                      <div className="mt-2 rounded-md bg-blue-50 p-2 text-sm text-blue-700 dark:bg-blue-900/20 dark:text-blue-300">
                        ✓ Lieferbeginn: Schnellstmöglich
                      </div>
                    )}
                  </div>

                  {/* Desired Monthly Payment */}
                  <div>
                    <label
                      htmlFor="gas_monthly_payment"
                      className="mb-2 block text-sm font-medium text-gray-900 dark:text-white"
                    >
                      gewünschter monatl. Abschlag
                    </label>
                    <TextInput
                      id="gas_monthly_payment"
                      type="number"
                      step="0.01"
                      value={gasDetails.desired_monthly_payment}
                      onChange={(e) =>
                        setGasDetails({
                          ...gasDetails,
                          desired_monthly_payment: e.target.value,
                        })
                      }
                      placeholder="Optional"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Contract Types Grid */}
            {selectedCategory && (
              <div className="mt-6">
                <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                  <h4 className="text-base font-medium text-gray-900 dark:text-white">
                    Verfügbare Produkte
                  </h4>
                  <div className="w-full md:max-w-md">
                    <div className="relative">
                      <TextInput
                        id="search-contract-types"
                        type="text"
                        icon={HiSearch}
                        placeholder="Produkte durchsuchen..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full"
                      />
                      {searchQuery && (
                        <button
                          type="button"
                          className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                          onClick={handleClearSearch}
                          aria-label="Suche löschen"
                        >
                          <HiX className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {loadingTypes ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Produkte werden geladen...
                  </p>
                ) : contractTypes.length > 0 ? (
                  <>
                    {/* Filtered contract types count */}
                    {searchQuery && (
                      <p className="mb-3 text-sm text-gray-600 dark:text-gray-400">
                        {filteredContractTypes.length} von{" "}
                        {contractTypes.length} Produkten gefunden
                      </p>
                    )}

                    {filteredContractTypes.length > 0 ? (
                      <>
                        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                          {paginatedContractTypes.map((contractType) => (
                            <CompactContractTypeCard
                              key={contractType.id}
                              contractType={contractType}
                              onAdd={() => addContractType(contractType)}
                              onRemove={() =>
                                removeContractType(contractType.id)
                              }
                              isSelected={selectedContractTypes.some(
                                (ct) => ct.id === contractType.id
                              )}
                            />
                          ))}
                        </div>

                        {/* Pagination */}
                        {totalPages > 1 && (
                          <div className="mt-4 flex justify-center">
                            <Pagination
                              currentPage={currentPage}
                              totalPages={totalPages}
                              onPageChange={handlePageChange}
                              showIcons
                            />
                          </div>
                        )}
                      </>
                    ) : (
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Keine Produkte gefunden für "{searchQuery}"
                      </p>
                    )}
                  </>
                ) : (
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Keine Produkte für diese Kategorie verfügbar
                  </p>
                )}
              </div>
            )}

            {/* Selected Contracts Section */}
            {selectedContractTypes.length > 0 && (
              <div className="mt-6 border-t border-gray-200 pt-6 dark:border-gray-700">
                <h4 className="mb-4 text-base font-medium text-gray-900 dark:text-white">
                  Ausgewählte Produkte ({selectedContractTypes.length})
                </h4>

                <div className="mb-4 flex flex-wrap gap-2">
                  {selectedContractTypes.map((contractType) => (
                    <Badge
                      key={contractType.id}
                      color="info"
                      className="flex items-center gap-2 px-3 py-1.5"
                      style={{
                        backgroundColor:
                          contractType.company_details.color_code,
                      }}
                    >
                      <span>
                        {contractType.type} -{" "}
                        {contractType.company_details.company}
                      </span>
                      <button
                        onClick={() => removeContractType(contractType.id)}
                        className="ml-1 rounded-full bg-white/20 p-0.5 hover:bg-white/40"
                        aria-label={`Entfernen von ${contractType.type}`}
                      >
                        <HiX className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>

                <div className="flex flex-col gap-3">
                  <div className="flex items-center gap-4">
                    {!signature ? (
                      <Button
                        color="primary"
                        onClick={() => setShowSignaturePad(true)}
                        disabled={!customer.birthdate}
                      >
                        Unterschreiben
                      </Button>
                    ) : (
                      <Button
                        color="success"
                        onClick={handleSaveClick}
                        disabled={isSaving}
                      >
                        {isSaving ? "Wird gespeichert..." : "Kunden speichern"}
                      </Button>
                    )}
                  </div>
                  {/* Alert for missing birth date */}
                  {!customer.birthdate && (
                    <div className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
                      Geburtsdatum erforderlich
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Signature Pad */}
      {showSignaturePad && (
        <SignaturePad
          onSave={handleSignatureSave}
          onCancel={() => setShowSignaturePad(false)}
        />
      )}

      {/* Confirmation Modal */}
      <ContractConfirmationModal
        show={showConfirmation}
        onClose={() => setShowConfirmation(false)}
        onConfirm={handleSaveContract}
        isLoading={isSaving}
        contractData={{
          contract: {
            firstName: customer.first_name,
            birthDate: customer.birthdate,
            email: customer.email,
            phone: customer.phone_number,
            bank_details: bankDetails,
          },
          name: customer.last_name,
          floor: "",
          comments: customer.notes,
          selectedContractTypes: selectedContractTypes,
        }}
        address={customer.address}
      />

      {/* Success/Error Modal for PDF and Email */}
      <ContractSuccessModal
        show={showSuccessModal}
        onClose={() => {
          setShowSuccessModal(false);
          setSuccessModalActionLabel(undefined);
        }}
        success={successModalSuccess}
        message={successModalMessage}
        actionLabel={successModalActionLabel}
        autoCloseDelay={successModalSuccess ? 3000 : 0}
      />
    </>
  );
};

export default NewContractModal;
